/* eslint-disable max-classes-per-file */
import isEmpty from 'lodash/isEmpty';
import { cssVars } from '@/app/styles';
import { calendarDataStatusOptions } from '@/constants';
import { MONTH_NAMES, SATURDAY, SUNDAY } from '@/list/list-constants';

const FIRST_LISTING = 0;

function getWeekendsInMonth(calendarActivity, dateStr) {
  const firstDayNumber = new Date(dateStr).getUTCDay();
  return calendarActivity.map((day, i) => {
    const dayNumber = (firstDayNumber + i) % 7;
    return dayNumber === SUNDAY || dayNumber === SATURDAY;
  });
}

/**
 * This function calculates values required for displaying Gantt chart in AgGrid
 * The relevant values are extracted from the first listing as we assume that all listings
 * will have same number of matching number of items for each month in their calendarActivities
 * dataPoint
 *
 * @param {Array} listingsData
 * @returns {Array<{ monthName, monthIndex, numDays, weekendsInMonth, firstDayNumber }>}
 *   monthName: e.g.: July
 *   monthIndex: where this month is in the dataPoints array
 *   numDays: number of days in the month
 *   weekendsInMonth: array of booleans where `true` means the day is a weekend. This array is
 *     parallel with the dataPoints[x].calendarActivities array
 *   firstDayNumber: what is the starting day for the month
 */
export function getCalendarActivityDetails(listingsData) {
  if (!listingsData || isEmpty(listingsData)) return [];
  // we assume all listings have the same number of months, and same months
  const firstListing = listingsData[FIRST_LISTING];
  const monthDetails = firstListing.dataPoints?.map((dp, monthIndex) => {
    const dateStr = dp.date;
    const dateObj = new Date(dateStr);
    const monthNumber = dateObj.getUTCMonth();
    const firstDayNumber = dateObj.getUTCDate();
    const monthName = MONTH_NAMES[monthNumber];
    const weekendsInMonth = getWeekendsInMonth(dp.calendarActivity, dateStr);
    const numDays = dp.calendarActivity.length;
    return { monthName, monthIndex, numDays, weekendsInMonth, firstDayNumber };
  });

  // Putting || [] here b/c there may be listing data without calendar data
  return monthDetails || [];
}

/**
 * Static properties and methods are of the "class" themselves, instead of the instance
 *  No need to extend a child class with this class
 */
class CalendarMgr {
  constructor() {
    this.isNotBookedColor = cssVars.color.calendarColor.transparent;

    // Colors shared between the list view and property details view calendars
    this.isBookedColors = [
      cssVars.color.calendarColor.normal,
      cssVars.color.calendarColor.light,
    ];
  }

  isNotBooked = (dayData) =>
    dayData.status !== calendarDataStatusOptions.BOOKED;

  isBlocked = (dayData) => dayData.status === calendarDataStatusOptions.BLOCKED;

  colorForCurrentDate = () =>
    this.isBookedColors[this.colorIndex % this.isBookedColors.length];

  /**
   * Returns the proper color for the date cell render
   * @param {Object} dayData rental data for a specific date
   * @param {Boolean} isCheckin if a specific date is first date of a rental period
   */
  colorGetter = (dayData, isCheckin) => {
    if (this.isBlocked(dayData)) return this.isBlockedColor;
    if (this.isNotBooked(dayData)) return this.isNotBookedColor;

    // Alternate colors by incrementing the index of the child class
    if (isCheckin) ++this.colorIndex;
    return this.colorForCurrentDate();
  };
}

/**
 * Extends the base class to retrieve the proper color for day cells
 *  of the list view calendars
 */
class ListViewCalenderMgr extends CalendarMgr {
  constructor(idx = 0) {
    super();
    this.colorIndex = idx;
    this.isBlockedColor = cssVars.color.calendarColor.blocked;
  }
}

export const listViewCalendarMgr = new ListViewCalenderMgr();

/**
 * Extends the base class to retrieve the proper color for day cells
 *  of the property details view calendars
 */
class PropertyDetailsCalenderMgr extends CalendarMgr {
  constructor(idx = 0) {
    super();
    this.colorIndex = idx;
    this.isBlockedColor = this.isNotBookedColor; // Get the parent's isNotBookedColor
  }
}

export const propertyDetailsCalendarMgr = new PropertyDetailsCalenderMgr();
